import { documentBody, type MarkdownDocument } from '@markbird/shared';
import { db, type DocMeta } from './db';
import { effectiveAliases, effectiveTags } from './metadata';
import type { LibrarySort } from './library';
import { compareManual } from './rank';

/**
 * The metadata projection: everything the library UI needs, minus the prose.
 *
 * Kept current from `notifyWrite`, the same choke point the sync engine uses, so
 * a new mutation cannot forget to update it. Reprojection is fire-and-forget:
 * this is derived data, and a list that lags a mutation by a millisecond is not
 * a correctness problem, whereas a missed call site would be.
 */

const VERSION_KEY = 'docMetaVersion';
// 2: rows gained `rank`.
const REBUILD_TOKEN = 2;

const EXCERPT_CHARS = 160;

/** Condense the start of a body into a single line for list previews. */
export function excerptOf(content: string): string {
  const body = documentBody(content)
    // Drop the leading heading: the title already says it.
    .replace(/^\s*#{1,6}\s+[^\n]*\n/, '')
    .replace(/\s+/g, ' ')
    .trim();
  return body.length > EXCERPT_CHARS ? `${body.slice(0, EXCERPT_CHARS).trimEnd()}…` : body;
}

/** Project a document onto its metadata row. */
export function projectDoc(doc: MarkdownDocument): DocMeta {
  return {
    id: doc.id,
    title: doc.title,
    createdAt: doc.createdAt,
    updatedAt: doc.updatedAt,
    ownerId: doc.ownerId,
    deletedAt: doc.deletedAt ?? null,
    pinned: doc.pinned ?? false,
    tags: effectiveTags(doc),
    aliases: effectiveAliases(doc),
    folder: doc.folder ?? null,
    rank: doc.rank ?? null,
    excerpt: excerptOf(doc.content),
    size: documentBody(doc.content).length,
  };
}

/** Refresh (or drop) one document's projection after a write. */
export async function reprojectDoc(id: string): Promise<void> {
  const doc = await db.documents.get(id);
  if (!doc) {
    await db.docmeta.delete(id);
    return;
  }
  await db.docmeta.put(projectDoc(doc));
}

/** Rebuild the whole projection from documents. */
export async function rebuildDocMeta(): Promise<void> {
  const docs = await db.documents.toArray();
  const rows = docs.map(projectDoc);
  await db.transaction('rw', db.docmeta, async () => {
    await db.docmeta.clear();
    if (rows.length) await db.docmeta.bulkPut(rows);
  });
}

/** Build the projection on first run, and after its shape changes. */
export async function ensureDocMeta(): Promise<void> {
  const row = await db.kv.get(VERSION_KEY);
  if (row?.value === REBUILD_TOKEN) return;
  await rebuildDocMeta();
  await db.kv.put({ key: VERSION_KEY, value: REBUILD_TOKEN });
}

/** Order metadata rows the way the library shows them: pinned first. */
export function sortMeta(rows: DocMeta[], sortBy: LibrarySort = 'updated'): DocMeta[] {
  const cmp = (a: DocMeta, b: DocMeta): number => {
    switch (sortBy) {
      case 'created':
        return b.createdAt - a.createdAt;
      case 'title':
        return (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
      case 'manual':
        return compareManual(a, b);
      case 'updated':
      default:
        return b.updatedAt - a.updatedAt;
    }
  };
  return [...rows].sort(
    (a, b) => Number(b.pinned ?? false) - Number(a.pinned ?? false) || cmp(a, b),
  );
}

/** Live (non-trashed) documents as metadata rows, in library order. */
export async function listDocumentMeta(sortBy: LibrarySort = 'updated'): Promise<DocMeta[]> {
  const rows = await db.docmeta.toArray();
  return sortMeta(
    rows.filter((r) => r.deletedAt == null),
    sortBy,
  );
}

/** Metadata for one document. */
export function getDocumentMeta(id: string): Promise<DocMeta | undefined> {
  return db.docmeta.get(id);
}
